export type EstadoRequisicao =
    | 'ENVIANDO'
    | 'FALHA_ENVIO'
    | 'SUBMETIDA'
    | 'PROCESSANDO'
    | 'CONCLUIDA'
    | 'CONCLUIDA_COM_ERROS'
    | 'PUBLICANDO'
    | 'PUBLICADA';

export const ESTADOS_NAO_TERMINAIS: EstadoRequisicao[] = [
    'SUBMETIDA',
    'PROCESSANDO',
    'PUBLICANDO'
];

export const ESTADOS_PRONTOS_PARA_PUBLICAR: EstadoRequisicao[] = [
    'CONCLUIDA',
    'CONCLUIDA_COM_ERROS'
];

// Matriz de roles do apm-extractor (src/api/auth.py do serviço), espelhada no
// proxy e aqui. Aceitas como client roles do client Keycloak "apm-extractor"
// ou como realm roles (prefixo 'realm.'), mesma tolerância do serviço remoto.
export const APM_EXTRACAO_ROLES_OPERADOR: string[] = [
    'apm-extractor.apm-operator',
    'apm-extractor.apm-admin',
    'realm.apm-operator',
    'realm.apm-admin'
];

export const APM_EXTRACAO_ROLES_ADMIN: string[] = [
    'apm-extractor.apm-admin',
    'realm.apm-admin'
];

export const APM_EXTRACAO_ROLES_PUBLICADOR: string[] = [
    'apm-extractor.apm-publisher',
    'apm-extractor.apm-admin',
    'realm.apm-publisher',
    'realm.apm-admin'
];

export const APM_EXTRACAO_ROLES_LEITURA: string[] = [
    'apm-extractor.apm-reader',
    'apm-extractor.apm-operator',
    'apm-extractor.apm-publisher',
    'apm-extractor.apm-admin',
    'realm.apm-reader',
    'realm.apm-operator',
    'realm.apm-publisher',
    'realm.apm-admin'
];

/**
 * Item do diretório de bairros para o select do formulário de extração.
 * `valor` é o que deve ser submetido como campo `bairro` da requisição;
 * `label` é o texto de exibição.
 */
export interface BairroDTO {
    valor: string;
    label: string;
    confianca: string;
}

export interface ArquivoEnviadoDTO {
    bucketName: string;
    bucketPath: string;
    originalFilename: string;
    tamanhoBytes: number;
    contentType: string;
}

export interface StatusSnapshotDTO {
    total: number;
    processados: number;
    concluidos: number;
    erros: number;
    summary: Record<string, unknown> | null;
    atualizadoEm: string;
}

export interface ResultadoPublicacaoDTO {
    documentos: number;
    citacoesResolvidas: number;
    citacoesAmbiguas: number;
    citacoesNaoResolvidas: number;
    stagingRemovidos: number;
    publicadoEm: string;
}

export interface ApmExtracaoDTO {
    id: string;
    bairro: string | null;
    estado: EstadoRequisicao;
    taskId: string | null;
    arquivos: ArquivoEnviadoDTO[];
    statusSnapshot: StatusSnapshotDTO | null;
    publicacao: ResultadoPublicacaoDTO | null;
    mensagemErro: string | null;
    criadoPor: string;
    criadoEm: string;
    atualizadoEm: string;
}

// --- Registro do preview (staging do apm-extractor, DocumentRecord) ---

export interface PreviewAssinatura {
    nome?: string | null;
    cargo?: string | null;
}

export interface PreviewApmCandidato {
    apm_rotulo?: string | null;
    codigo_mapa_digital?: string | null;
    inscricao_apm?: string | null;
    bairro?: string | null;
    qd?: string | null;
    lt?: string | null;
    logradouro?: string | null;
    tipo_propriedade?: string | null;
    nr_iptu?: string | null;
    destinacao_original?: string | null;
    uso_atual?: string | null;
    area_total?: string | null;
}

export type StatusResolucaoApm = 'resolvido' | 'ambiguo' | 'nao_resolvido' | 'invalida';

export type StatusDocumento = 'resolvido' | 'parcialmente_resolvido' | 'nao_resolvido';

export interface PreviewApmCitada {
    citacao_id?: string | null;
    inscricao_apm?: string | null;
    total_candidatos?: number | null;
    revisao?: Record<string, unknown> | null;
    apm_id_citado?: string | null;
    apm_rotulo?: string | null;
    codigo_mapa_digital?: string | null;
    unidade?: string | null;
    entre_ruas?: string[] | null;
    quadra?: string | null;
    lote?: string | null;
    motivo_citacao?: string | null;
    periodo_solicitado_texto?: string | null;
    data_solicitada_inicio?: string | null;
    data_solicitada_fim?: string | null;
    origem?: string | null;
    status_resolucao?: StatusResolucaoApm;
    metodo_resolucao?: string | null;
    motivo?: string | null;
    sinais_divergentes?: string[] | null;
    candidatos?: PreviewApmCandidato[] | null;
}

export interface PreviewUsage {
    model?: string | null;
    input_tokens?: number | null;
    output_tokens?: number | null;
    cost_usd?: number | null;
    elapsed_s?: number | null;
}

export interface PreviewRegistro {
    doc_id?: string | null;
    status_documento?: StatusDocumento | string | null;
    bairro?: string | null;
    bairro_extraido?: string | null;
    bairros_candidatos?: string[] | null;
    revisao?: Record<string, unknown> | null;
    original_filename?: string | null;
    final_filename?: string | null;
    source_path?: string | null;
    bucket_name?: string | null;
    bucket_path?: string | null;
    data_documento?: string | null;
    assunto?: string | null;
    interessado?: string | null;
    assinaturas?: PreviewAssinatura[] | null;
    content_hash?: string | null;
    usage?: PreviewUsage | null;
    apms?: PreviewApmCitada[] | null;
}

export function statusResolucaoLabel(status: StatusResolucaoApm | undefined): string {
    switch (status) {
        case 'resolvido': return 'Resolvida';
        case 'ambiguo': return 'Ambígua';
        case 'nao_resolvido': return 'Não resolvida';
        case 'invalida': return 'Inválida';
        default: return 'Sem status';
    }
}

export function statusResolucaoSeverity(
    status: StatusResolucaoApm | undefined
): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
        case 'resolvido': return 'success';
        case 'ambiguo': return 'warn';
        case 'nao_resolvido': return 'danger';
        case 'invalida': return 'secondary';
        default: return 'secondary';
    }
}

// --- Inspeção de documentos (tela de gavetas) ---

/** Citações que ainda travam o publish: nem resolvidas nem invalidadas. */
const STATUS_CITACAO_FECHADOS: ReadonlyArray<string> = ['resolvido', 'invalida'];

export function citacoesPendentes(registro: PreviewRegistro): PreviewApmCitada[] {
    return (registro.apms ?? []).filter(
        (apm) => !STATUS_CITACAO_FECHADOS.includes(apm.status_resolucao ?? '')
    );
}

/** Campo ausente (task legada) conta como NÃO resolvido — o servidor é a autoridade. */
export function documentoResolvido(registro: PreviewRegistro): boolean {
    return registro.status_documento === 'resolvido';
}

export function todosResolvidos(records: PreviewRegistro[]): boolean {
    return records.length > 0 && records.every(documentoResolvido);
}

export function statusDocumentoLabel(status: StatusDocumento | string | null | undefined): string {
    switch (status) {
        case 'resolvido': return 'Resolvido';
        case 'parcialmente_resolvido': return 'Parcialmente resolvido';
        case 'nao_resolvido': return 'Não resolvido';
        default: return 'Sem status';
    }
}

export function statusDocumentoSeverity(
    status: StatusDocumento | string | null | undefined
): 'success' | 'warn' | 'danger' | 'secondary' {
    switch (status) {
        case 'resolvido': return 'success';
        case 'parcialmente_resolvido': return 'warn';
        case 'nao_resolvido': return 'danger';
        default: return 'secondary';
    }
}

export interface CatalogoApmItem {
    inscricao_apm: string | null;
    codigo_mapa_digital: string | null;
    apm_rotulo: string | null;
    bairro: string | null;
    qd: string | null;
    lt: string | null;
    logradouro: string | null;
    tipo_propriedade: string | null;
    nr_iptu: string | null;
    destinacao_original: string | null;
    uso_atual: string | null;
    area_total: string | null;
}

export interface CatalogoResponse {
    total: number;
    page: number;
    page_size: number;
    versao_catalogo: string;
    itens: CatalogoApmItem[];
}

export type RevisaoCitacaoRequest =
    | { acao: 'escolher'; codigo_mapa_digital: string; inscricao_apm?: string | null; observacao?: string | null }
    | { acao: 'invalidar'; observacao: string };

export type RevisaoDocumentoRequest =
    | { acao: 'corrigir_bairro'; bairro: string; observacao?: string | null }
    | { acao: 'encerrar_sem_apm'; observacao: string }
    | { acao: 'reabrir'; observacao?: string | null };

export interface RevisaoResponse {
    record: PreviewRegistro;
}

export interface PreviewResponse {
    task_id: string;
    total: number;
    records: PreviewRegistro[];
}

const LABELS: Record<EstadoRequisicao, string> = {
    ENVIANDO: 'Enviando',
    FALHA_ENVIO: 'Falha no envio',
    SUBMETIDA: 'Na fila',
    PROCESSANDO: 'Processando',
    CONCLUIDA: 'Concluída',
    CONCLUIDA_COM_ERROS: 'Concluída com erros',
    PUBLICANDO: 'Publicando',
    PUBLICADA: 'Publicada'
};

export function estadoLabel(estado: EstadoRequisicao): string {
    return LABELS[estado] ?? estado;
}

export function estadoSeverity(
    estado: EstadoRequisicao
): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (estado) {
        case 'PUBLICADA':
            return 'success';
        case 'CONCLUIDA':
            return 'info';
        case 'CONCLUIDA_COM_ERROS':
            return 'warn';
        case 'FALHA_ENVIO':
            return 'danger';
        default:
            return 'secondary';
    }
}
